<script lang="ts" setup>
import {
  PhClockCounterClockwise,
  PhCoins,
  PhFiles,
  PhInfo,
} from '@phosphor-icons/vue';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { AtaDetailsCard } from '../sub-components/ata-details-card';
import { AtaDetailsSection } from '../sub-components/ata-details-section';

const { t } = useI18n();

const FIELD_ROWS = [0, 1, 2, 3];
const COST_ROWS = [0, 1, 2, 3, 4];
const CHANGELOG_ROWS = [0, 1, 2];

const generalMainRef = ref<HTMLElement | null>(null);
const generalDocHeight = ref<number | null>(null);

let generalMainResizeObserver: ResizeObserver | null = null;

const shouldSyncGeneralDocHeight = () =>
  typeof window !== 'undefined' && window.innerWidth > 1200;

const syncGeneralDocHeight = () => {
  if (!shouldSyncGeneralDocHeight()) {
    generalDocHeight.value = null;
    return;
  }
  generalDocHeight.value = generalMainRef.value?.offsetHeight ?? null;
};

onMounted(() => {
  syncGeneralDocHeight();

  if (typeof window !== 'undefined') {
    window.addEventListener('resize', syncGeneralDocHeight);
  }

  if (generalMainRef.value && typeof ResizeObserver !== 'undefined') {
    generalMainResizeObserver = new ResizeObserver(() => {
      syncGeneralDocHeight();
    });
    generalMainResizeObserver.observe(generalMainRef.value);
  }
});

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', syncGeneralDocHeight);
  }

  if (generalMainResizeObserver) {
    generalMainResizeObserver.disconnect();
    generalMainResizeObserver = null;
  }
});
</script>

<template>
  <div class="ata-skeleton flex-grid-page" aria-hidden="true">
    <div class="header mb-4">
      <div class="skeleton-cell title" />
      <hr class="ata-title-divider" />
    </div>

    <div class="sections-layout">
      <AtaDetailsSection
        :icon="PhInfo"
        :title="t('ata.details.sections.generalInformation')"
      >
        <div class="general-layout">
          <div ref="generalMainRef" class="general-main">
            <AtaDetailsCard
              :icon="PhInfo"
              :title="t('ata.details.cards.ataInformation')"
            >
              <div class="columns is-multiline">
                <div class="column is-half">
                  <div class="skeleton-cell label" />
                  <div class="skeleton-cell input" />
                </div>
                <div class="column is-half">
                  <div class="skeleton-cell label" />
                  <div class="skeleton-cell input" />
                </div>
                <div class="column is-full">
                  <div class="skeleton-cell label" />
                  <div class="skeleton-cell textarea" />
                </div>
                <div class="column is-full">
                  <div class="skeleton-cell label" />
                  <div class="skeleton-cell textarea" />
                </div>
              </div>
              <div class="columns is-multiline">
                <div v-for="i in FIELD_ROWS" :key="i" class="column is-half">
                  <div class="skeleton-cell label" />
                  <div class="skeleton-cell input" />
                </div>
              </div>
            </AtaDetailsCard>
          </div>

          <div
            class="general-doc"
            :style="
              generalDocHeight
                ? {
                    height: `${generalDocHeight}px`,
                  }
                : undefined
            "
          >
            <AtaDetailsCard
              :icon="PhFiles"
              :title="t('ata.details.cards.ataDocuments')"
            >
              <div class="skeleton-cell doc-block" />
            </AtaDetailsCard>
          </div>
        </div>
      </AtaDetailsSection>

      <AtaDetailsSection
        :icon="PhCoins"
        :title="t('ata.details.sections.costs')"
      >
        <AtaDetailsCard
          :icon="PhCoins"
          :title="t('ata.details.cards.ataCosts')"
          content-class="no-padding"
        >
          <div class="skeleton-rows">
            <div v-for="i in COST_ROWS" :key="i" class="skeleton-cell row" />
          </div>
        </AtaDetailsCard>
      </AtaDetailsSection>

      <AtaDetailsSection
        :icon="PhClockCounterClockwise"
        :title="t('ata.changelogSectionTitle')"
      >
        <AtaDetailsCard
          :icon="PhClockCounterClockwise"
          :title="t('ata.changelogTitle')"
          content-class="no-padding"
        >
          <div class="skeleton-rows">
            <div
              v-for="i in CHANGELOG_ROWS"
              :key="i"
              class="skeleton-cell row"
            />
          </div>
        </AtaDetailsCard>
      </AtaDetailsSection>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.flex-grid-page {
  --bulma-column-gap: 0.5rem;
  --doc-handler-max-width: 36rem;
  --doc-handler-min-width: 34rem;

  display: flex;
  flex-direction: column;
  row-gap: 1rem;

  .sections-layout {
    display: flex;
    flex-direction: column;
    gap: 4rem;
  }

  .ata-title-divider {
    width: 100%;
    margin: 0.5rem 0 0;
    border: none;
    border-top: 1px solid #dbdbdb;
  }

  .general-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) fit-content(
        var(--doc-handler-max-width)
      );
    gap: 1.5rem;
    align-items: start;
  }

  .general-main {
    min-width: 0;
  }

  .general-doc {
    min-width: var(--doc-handler-min-width);
    width: fit-content;
    max-width: var(--doc-handler-max-width);
    justify-self: start;
    display: flex;

    :deep(.ata-details-card) {
      display: flex;
      flex-direction: column;
      width: 100%;
      min-height: 0;
    }

    :deep(.card-content) {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      min-height: 0;
    }
  }

  @media (max-width: 1200px) {
    .general-layout {
      grid-template-columns: minmax(0, 1fr);
    }

    .general-doc {
      min-width: 0;
      width: 100%;
    }
  }

  :deep(.no-padding) {
    padding: 0;
    padding-top: 0.75rem;
    overflow: hidden;
  }
}

.skeleton-cell {
  border-radius: 4px;
  background: linear-gradient(90deg, #ebebeb 25%, #f5f5f5 50%, #ebebeb 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s ease-in-out infinite;
}

.title {
  height: 2rem;
  width: 40%;
}

.label {
  height: 0.9rem;
  width: 30%;
  margin-bottom: 0.5rem;
}

.input {
  height: 2.25rem;
  width: 100%;
}

.textarea {
  height: 4.5rem;
  width: 100%;
}

.doc-block {
  flex: 1 1 auto;
  min-height: 12rem;
  width: 100%;
}

.skeleton-rows {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1rem 1rem;

  .row {
    height: 2rem;
    width: 100%;
  }
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
